<template>
  <div class="page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <div class="header-left">
          <span>课程评价</span>
          <el-button size="small" type="primary" :loading="exporting" @click="exportCsv">导出</el-button>
        </div>
        <el-input
          v-model="keyword"
          clearable
          placeholder="课程 / 姓名 / 学校 / 专业"
          style="width: 260px"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch"
        />
      </div>
      <el-table v-loading="loading" :data="list" border stripe>
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="courseTitle" label="课程" min-width="180" show-overflow-tooltip />
        <el-table-column prop="studentName" label="姓名" min-width="100" show-overflow-tooltip />
        <el-table-column prop="contact" label="联系方式" min-width="130" show-overflow-tooltip />
        <el-table-column prop="school" label="所在学校" min-width="140" show-overflow-tooltip />
        <el-table-column prop="major" label="专业" min-width="120" show-overflow-tooltip />
        <el-table-column label="学员" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <div class="who-cell">
              <span class="who-name">{{ row.userNickname || '用户' }}</span>
              <span class="who-phone">{{ row.userPhone || row.userId }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="课程" width="118">
          <template slot-scope="{ row }">
            <span class="mini-stars"><i v-for="n in 5" :key="'c'+row.id+n" :class="{ on: n <= Number(row.courseStars || 0) }">★</i></span>
          </template>
        </el-table-column>
        <el-table-column label="教师" width="118">
          <template slot-scope="{ row }">
            <span class="mini-stars"><i v-for="n in 5" :key="'t'+row.id+n" :class="{ on: n <= Number(row.teacherStars || 0) }">★</i></span>
          </template>
        </el-table-column>
        <el-table-column label="课程类型" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }"><span>{{ shortType(row.courseType) }}</span></template>
        </el-table-column>
        <el-table-column label="宸镜AI" width="88">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.willingAi === '是' ? 'success' : 'info'" effect="plain">{{ row.willingAi || '—' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="产学研" width="88">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.willingIndustry === '是' ? 'success' : 'info'" effect="plain">{{ row.willingIndustry || '—' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="courseComment" label="课程评价" min-width="180" show-overflow-tooltip />
        <el-table-column prop="createdAt" label="提交时间" min-width="150" :formatter="formatTime" />
        <el-table-column label="操作" width="80" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">查看</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="load"
      />
    </el-card>

    <el-dialog
      :visible.sync="visible"
      width="760px"
      top="7vh"
      custom-class="review-dialog"
      append-to-body
    >
      <div slot="title" class="dlg-title">评价详情</div>
      <div v-if="detail" class="sheet">
        <div class="hero">
          <div>
            <div class="course">{{ detail.courseTitle || '未命名课程' }}</div>
            <div class="meta">课程 ID {{ detail.articleId }} · {{ formatTime(null, null, detail.createdAt) || '刚刚提交' }}</div>
          </div>
          <div class="person">
            <span class="avatar">{{ initial(detail.userNickname) }}</span>
            <div>
              <div class="name">{{ detail.userNickname || '用户' }}</div>
              <div class="phone">{{ detail.userPhone || ('用户 ' + detail.userId) }}</div>
            </div>
          </div>
        </div>

        <div class="profile">
          <div><span>姓名</span><strong>{{ detail.studentName || '未填写' }}</strong></div>
          <div><span>联系方式</span><strong>{{ detail.contact || '未填写' }}</strong></div>
          <div><span>所在学校</span><strong>{{ detail.school || '未填写' }}</strong></div>
          <div><span>专业</span><strong>{{ detail.major || '未填写' }}</strong></div>
        </div>

        <div class="score-grid">
          <section class="score-card">
            <header>
              <span>课程评分</span>
              <strong>{{ detail.courseStars || 0 }}</strong>
            </header>
            <div class="stars"><i v-for="n in 5" :key="'cs'+n" :class="{ on: n <= Number(detail.courseStars || 0) }">★</i></div>
            <p>{{ detail.courseComment || '未填写课程评价' }}</p>
          </section>
          <section class="score-card">
            <header>
              <span>教师评分</span>
              <strong>{{ detail.teacherStars || 0 }}</strong>
            </header>
            <div class="stars"><i v-for="n in 5" :key="'ts'+n" :class="{ on: n <= Number(detail.teacherStars || 0) }">★</i></div>
            <p>{{ detail.teacherComment || '未填写教师评价' }}</p>
          </section>
        </div>

        <section class="learn">
          <div class="learn-label">学到了什么</div>
          <p>{{ detail.learnedContent || '未填写' }}</p>
        </section>

        <div class="split">
          <section>
            <div class="learn-label">希望拓展</div>
            <p>{{ detail.expandField || '未填写' }}</p>
          </section>
          <section>
            <div class="learn-label">就业方向</div>
            <p>{{ detail.careerDirection || '未填写' }}</p>
          </section>
        </div>

        <div class="choices">
          <div class="choice">
            <span>愿意使用宸镜 AI</span>
            <em :class="detail.willingAi === '是' ? 'yes' : 'no'">{{ detail.willingAi || '未选' }}</em>
          </div>
          <div class="choice">
            <span>愿意参加产学研</span>
            <em :class="detail.willingIndustry === '是' ? 'yes' : 'no'">{{ detail.willingIndustry || '未选' }}</em>
          </div>
          <div class="choice type">
            <span>课程类型</span>
            <em class="type-name">{{ shortType(detail.courseType) }}</em>
            <small v-if="detail.courseType && shortType(detail.courseType) !== detail.courseType">{{ detail.courseType }}</small>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { exportCourseReviews, getCourseReviewDetail, getCourseReviews } from '@/api/admin'

export default {
  name: 'CourseReviews',
  data() {
    return {
      keyword: '',
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      loading: false,
      exporting: false,
      visible: false,
      detail: null
    }
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await getCourseReviews({
          keyword: this.keyword || undefined,
          page: this.page,
          size: this.pageSize
        })
        this.list = res.list || []
        this.total = res.total || 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.load()
    },
    async exportCsv() {
      this.exporting = true
      try {
        const csv = await exportCourseReviews({ keyword: this.keyword || undefined })
        const blob = new Blob(['\ufeff' + (csv || '')], { type: 'text/csv;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = '课程评价.csv'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } finally {
        this.exporting = false
      }
    },
    async openDetail(row) {
      this.detail = await getCourseReviewDetail(row.id)
      this.visible = true
    },
    shortType(value) {
      const text = value || ''
      if (text.indexOf('7课时') === 0) return '7 课时兴趣班'
      if (text.indexOf('21课时') === 0) return '21 课时 · 训练师四级'
      if (text.indexOf('60课时') === 0) return '60 课时 · 提高班'
      if (text.indexOf('90课时') === 0) return '90 课时 · 训练师三级'
      return text || '未选择'
    },
    initial(name) {
      const text = (name || '用').trim()
      return text.slice(0, 1)
    },
    formatTime(row, column, value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      const pad = (n) => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.card-header { display: flex; align-items: center; justify-content: space-between; }
.header-left { display: flex; align-items: center; gap: 12px; }
.pagination { margin-top: 16px; text-align: right; }
.who-cell { display: flex; flex-direction: column; line-height: 1.35; }
.who-name { color: #1f2937; }
.who-phone { color: #94a3b8; font-size: 12px; }
.mini-stars i { font-style: normal; color: #e2e8f0; letter-spacing: 1px; font-size: 13px; }
.mini-stars i.on { color: #f59e0b; }
</style>

<style>
.review-dialog { border-radius: 16px; overflow: hidden; }
.review-dialog .el-dialog__header { padding: 18px 22px 0; }
.review-dialog .el-dialog__body { padding: 12px 22px 22px; }
.review-dialog .dlg-title { font-size: 16px; font-weight: 650; color: #0f172a; }
.review-dialog .hero {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef6f4 100%);
}
.review-dialog .course { font-size: 16px; font-weight: 650; color: #0f172a; line-height: 1.4; }
.review-dialog .meta { margin-top: 4px; color: #64748b; font-size: 12px; }
.review-dialog .profile {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e8eef3;
}
.review-dialog .profile span { display: block; color: #64748b; font-size: 12px; }
.review-dialog .profile strong { display: block; margin-top: 2px; color: #0f172a; font-weight: 600; }
.review-dialog .person { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.review-dialog .avatar {
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: #0f766e; color: #fff; font-weight: 650;
}
.review-dialog .name { color: #0f172a; font-weight: 600; }
.review-dialog .phone { color: #64748b; font-size: 12px; margin-top: 2px; }
.review-dialog .score-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 14px; }
.review-dialog .score-card {
  border: 1px solid #e8eef3;
  border-radius: 12px;
  padding: 12px 14px 14px;
  background: #fff;
}
.review-dialog .score-card header { display: flex; align-items: baseline; justify-content: space-between; }
.review-dialog .score-card header span { color: #64748b; font-size: 13px; }
.review-dialog .score-card header strong { color: #0f172a; font-size: 22px; line-height: 1; }
.review-dialog .stars { margin: 6px 0 8px; letter-spacing: 2px; font-size: 16px; }
.review-dialog .stars i { font-style: normal; color: #e2e8f0; }
.review-dialog .stars i.on { color: #f59e0b; }
.review-dialog .score-card p,
.review-dialog .learn p,
.review-dialog .split p {
  margin: 0;
  color: #334155;
  line-height: 1.65;
  white-space: pre-wrap;
  word-break: break-word;
}
.review-dialog .learn,
.review-dialog .split section {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
}
.review-dialog .learn-label { margin-bottom: 6px; color: #64748b; font-size: 12px; }
.review-dialog .split { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.review-dialog .choices {
  display: grid;
  grid-template-columns: 160px 160px 1fr;
  gap: 10px;
  margin-top: 12px;
}
.review-dialog .choice {
  min-height: 72px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
}
.review-dialog .choice span { color: #64748b; font-size: 12px; }
.review-dialog .choice em {
  font-style: normal;
  font-weight: 650;
  width: fit-content;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 13px;
}
.review-dialog .choice em.yes { color: #047857; background: #d1fae5; }
.review-dialog .choice em.no { color: #64748b; background: #e2e8f0; }
.review-dialog .choice em.type-name { color: #0f766e; background: #ccfbf1; }
.review-dialog .choice small { color: #94a3b8; font-size: 12px; line-height: 1.4; }
</style>
